import { useEffect, useState } from 'react';
import { Button, Select, TextField } from '@nyce/admin-kit/ui';
import { Link } from 'react-router-dom';
import { NumberField, SwitchField } from '@nyce/admin-kit/console';
import {
  DEFAULT_ROLE_CLAIM,
  type SamlSettings,
  type SamlUserMatch,
  type SamlUserSource,
  type Tenant,
} from '../../../identity/api';
import { guidePath } from '../../../onboarding/guides';
import { client } from '../../../identity/session';
import { fmtDate } from '../../../identity/format';
import { Code, ConfirmButton, EmptyState, ErrorBox, Loading, useAsync } from '../../../components/common';
import { CopyRow } from './ScimConnection';

type Notify = { success: (t: string) => void; error: (t: string) => void };
type Source = 'url' | 'xml' | 'keep';

const SOURCES: { value: SamlUserSource; label: string }[] = [
  { value: 'Existing', label: 'Provisioned (SCIM) or created here' },
  { value: 'Assertion', label: 'Created at sign-in, roles from Entra app roles' },
];

const MATCH: { value: SamlUserMatch; label: string }[] = [
  { value: 'Username', label: 'NameID = username (Entra: user principal name)' },
  { value: 'ExternalId', label: 'Entra object id = external id (SCIM externalId)' },
];

/**
 * Federated sign-in of a Database tenant through its SAML identity provider (Entra ID, `docs/saml-entra.md`): what
 * Entra needs from AUTH (copy), the IdP's metadata (URL, re-read on every save, or pasted XML), how an assertion
 * names a local user, and on/off. Users are not created by a sign-in: SCIM (or the users tab) creates them.
 */
export function SsoTab({ tenant, canWrite, notify }: { tenant: Tenant; canWrite: boolean; notify: Notify }) {
  const settings = useAsync(() => client.tenants.saml.get(tenant.id), [tenant.id]);
  if (tenant.identityProvider !== 'Database')
    return (
      <EmptyState
        card
        title="Single sign-on needs the Database identity provider"
        hint={
          <>
            A Workserver tenant's users sign in at its WorkServer, which has its own Microsoft sign-in (set up
            in Entra and the WorkServer options, users provisioned into the WorkServer over SCIM):{' '}
            <Link to={guidePath('entra-sso-nycelogic', { workserver: tenant.name })}>
              Entra sign-in for nyce.logic WMS
            </Link>
            .
          </>
        }
      />
    );
  return (
    <div className="nyk-card" data-testid="sso-tab">
      <div className="nyk-card-head">
        <h2>Single sign-on (SAML)</h2>
        <span className="nyk-ui-hint">Entra ID or another SAML 2.0 identity provider</span>
      </div>
      <ErrorBox error={settings.error} />
      <Loading loading={settings.loading && !settings.data} />
      {settings.data && (
        <SsoForm
          tenant={tenant}
          value={settings.data}
          canWrite={canWrite}
          notify={notify}
          onSaved={(next) => settings.setData(next)}
          onRemoved={() => void settings.reload()}
        />
      )}
    </div>
  );
}

function SsoForm({
  tenant,
  value,
  canWrite,
  notify,
  onSaved,
  onRemoved,
}: {
  tenant: Tenant;
  value: SamlSettings;
  canWrite: boolean;
  notify: Notify;
  onSaved: (s: SamlSettings) => void;
  onRemoved: () => void;
}) {
  const [enabled, setEnabled] = useState(value.configured ? value.enabled : true);
  const [matchBy, setMatchBy] = useState<SamlUserMatch>(value.matchBy);
  const [allowPassword, setAllowPassword] = useState(value.allowPassword);
  const [userSource, setUserSource] = useState<SamlUserSource>(value.userSource);
  const [roleClaim, setRoleClaim] = useState(value.roleClaim || DEFAULT_ROLE_CLAIM);
  const [requireRole, setRequireRole] = useState(value.requireRole);
  const [reauthenticateHours, setReauthenticateHours] = useState<number | null>(value.reauthenticateHours);
  const fromAssertion = userSource === 'Assertion';
  const [source, setSource] = useState<Source>(value.configured ? 'keep' : 'url');
  const [url, setUrl] = useState(value.metadataUrl ?? '');
  const [xml, setXml] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setEnabled(value.configured ? value.enabled : true);
    setMatchBy(value.matchBy);
    setAllowPassword(value.allowPassword);
    setUserSource(value.userSource);
    setRoleClaim(value.roleClaim || DEFAULT_ROLE_CLAIM);
    setRequireRole(value.requireRole);
    setReauthenticateHours(value.reauthenticateHours);
  }, [value]);

  const ready =
    (source === 'keep' || (source === 'url' ? !!url.trim() : !!xml.trim())) &&
    (!fromAssertion || (reauthenticateHours != null && !!roleClaim.trim()));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await client.tenants.saml.put(tenant.id, {
        enabled,
        // Users from the assertion are always found by their Entra object id.
        matchBy: fromAssertion ? 'ExternalId' : matchBy,
        allowPassword,
        userSource,
        roleClaim: fromAssertion ? roleClaim.trim() : undefined,
        requireRole: fromAssertion && requireRole,
        reauthenticateHours: fromAssertion ? (reauthenticateHours ?? 12) : value.reauthenticateHours,
        metadataUrl: source === 'url' ? url.trim() : undefined,
        metadataXml: source === 'xml' ? xml.trim() : undefined,
      });
      setSource('keep');
      setXml('');
      onSaved(saved);
      notify.success(`Single sign-on ${saved.enabled ? 'enabled' : 'saved (off)'} for ${tenant.name}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sources = [
    ...(value.configured ? [{ value: 'keep', label: 'Keep the stored metadata' }] : []),
    { value: 'url', label: 'App Federation Metadata URL' },
    { value: 'xml', label: 'Paste the metadata XML' },
  ];

  return (
    <>
      <p className="nyk-card-intro">
        In Entra ID: create an enterprise application (non-gallery), choose SAML, and under Basic SAML
        Configuration upload the metadata file or paste the Identifier and Reply URL below. Assign the users
        or groups that also reach this tenant over SCIM; a user who signs in must exist here and be active —
        or let sign-in create the users (Entra-only, below). Step by step:{' '}
        <Link
          to={guidePath('entra-sso', {
            tenantName: tenant.name,
            entityId: value.sp.entityId,
            acsUrl: value.sp.acsUrl,
          })}
        >
          Entra single sign-on guide
        </Link>
        .
      </p>
      <div className="nyk-form">
        <div className="nyk-span2">
          <CopyRow
            label="Identifier (Entity ID) and metadata URL"
            value={value.sp.entityId}
            testId="sso-entity-id"
          />
        </div>
        <div className="nyk-span2">
          <CopyRow label="Reply URL (Assertion Consumer Service)" value={value.sp.acsUrl} testId="sso-acs" />
        </div>
      </div>

      {value.configured && value.idp && (
        <table className="nyk-kv" aria-label="Identity provider" style={{ marginTop: 16 }}>
          <tbody>
            <tr>
              <th>Identity provider</th>
              <td>
                <Code>{value.idp.entityId}</Code>
              </td>
            </tr>
            <tr>
              <th>Sign-on URL</th>
              <td>{value.idp.ssoUrl}</td>
            </tr>
            <tr>
              <th>Signing certificates</th>
              <td>
                {value.idp.certificates.map((c) => (
                  <div key={c.thumbprint}>
                    {c.subject} · expires {fmtDate(c.notAfter)} · <Code>{c.thumbprint}</Code>
                  </div>
                ))}
              </td>
            </tr>
            <tr>
              <th>Metadata</th>
              <td>
                {value.metadataUrl ? (
                  <>
                    <div className="nyk-wrap">{value.metadataUrl}</div>
                    <div className="nyk-ui-hint">
                      Re-read daily and when a sign-in fails to verify · last read{' '}
                      {fmtDate(value.metadataFetchedAt ?? '')}
                    </div>
                    {value.metadataError && (
                      <ErrorBox
                        error={`Last read failed (${fmtDate(value.metadataCheckedAt ?? '')}): ${value.metadataError} — the certificates above stay in use.`}
                      />
                    )}
                  </>
                ) : (
                  'pasted XML (not refreshed: paste it again after a certificate change)'
                )}
              </td>
            </tr>
            <tr>
              <th>Updated</th>
              <td>
                {fmtDate(value.updatedAt ?? '')} by <Code>{value.updatedBy ?? ''}</Code>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {canWrite && (
        <>
          <div className="nyk-form" style={{ marginTop: 16 }}>
            <Select
              label="Identity provider metadata"
              value={source}
              onChange={(v) => setSource(v as Source)}
              options={sources}
            />
            <Select
              label="Users"
              value={userSource}
              onChange={(v) => setUserSource(v as SamlUserSource)}
              options={SOURCES}
              hint={
                fromAssertion
                  ? 'No SCIM: a sign-in creates or updates the user (found by Entra object id) and replaces its roles with the app roles Entra sends. New role names appear in the roles tab without scopes.'
                  : 'A user who signs in must already exist and be active.'
              }
            />
            {fromAssertion ? (
              <>
                <TextField
                  label="Role claim"
                  value={roleClaim}
                  onChange={(e) => setRoleClaim(e.target.value)}
                  hint="Entra sends app roles in this attribute (the default)."
                />
                <NumberField
                  label="Sign in again after"
                  value={reauthenticateHours}
                  onChange={setReauthenticateHours}
                  min={0}
                  max={720}
                  integer
                  unit="hours"
                  hint="AUTH hears of a removed user or role only at a sign-in, so sessions end after this (0 = never); the next one is silent while the Entra session lasts."
                />
                <SwitchField
                  label="Require an app role"
                  checked={requireRole}
                  onChange={setRequireRole}
                  hint="A sign-in without any app role is refused."
                />
              </>
            ) : (
              <Select
                label="Match users by"
                value={matchBy}
                onChange={(v) => setMatchBy(v as SamlUserMatch)}
                options={MATCH}
              />
            )}
            {source === 'url' && (
              <TextField
                fieldClassName="nyk-span2"
                label="Metadata URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://login.microsoftonline.com/<tenant>/federationmetadata/2007-06/federationmetadata.xml?appid=…"
                hint="Entra: SAML Certificates → App Federation Metadata Url. AUTH re-reads it daily, so certificate rollovers need nothing here."
              />
            )}
            {source === 'xml' && (
              <TextField
                fieldClassName="nyk-span2"
                multiline
                rows={6}
                label="Metadata XML"
                value={xml}
                onChange={(e) => setXml(e.target.value)}
                hint="Entra: SAML Certificates → Federation Metadata XML (download, paste here)."
              />
            )}
            <SwitchField
              label="Enabled"
              checked={enabled}
              onChange={setEnabled}
              hint="Off keeps the settings; sign-in through the IdP is refused."
            />
            <SwitchField
              label="Allow password sign-in"
              checked={allowPassword}
              onChange={setAllowPassword}
              hint="Off = Entra-only: passwords are refused while single sign-on is enabled."
            />
          </div>
          <ErrorBox error={error} />
          <div className="nyk-actions">
            <Button loading={busy} disabled={!ready} onClick={() => void save()}>
              Save
            </Button>
            {value.configured && (
              <ConfirmButton
                variant="danger"
                message={`Remove single sign-on from ${tenant.name}? Its users can no longer sign in through the identity provider.`}
                onConfirm={() =>
                  void client.tenants.saml
                    .remove(tenant.id)
                    .then(() => {
                      notify.success('Single sign-on removed');
                      onRemoved();
                    })
                    .catch((e: Error) => setError(e.message))
                }
              >
                Remove
              </ConfirmButton>
            )}
          </div>
        </>
      )}
    </>
  );
}
